/**
 * Canonical brand resolution helper for Koordinator app
 *
 * Resolves: current authenticated user → authorized brand context
 *
 * Strategy:
 *   1. Primary: brand_users table (owner/admin role, active status)
 *   2. Fallback: legacy brands.user_id direct ownership
 *
 * All koordinator pages/hooks that need a brand context should use this
 * instead of inline queries. This prevents auth model drift.
 */
import { supabase } from './supabase';

export interface ResolvedBrand {
  brandId: string;
  source: 'brand_users' | 'legacy_owner';
}

/**
 * Resolve ALL brand IDs the user is authorized to manage.
 * Combines brand_users membership (owner/admin/active) with legacy
 * brands.user_id ownership. Used by list views (orders, deals, etc.) so
 * multi-brand users see data across every brand they manage instead of
 * silently being scoped to one arbitrary brand.
 */
export async function resolveAllBrandIdsForUser(userId: string): Promise<string[]> {
  const ids = new Set<string>();

  const { data: bu } = await supabase
    .from('brand_users')
    .select('brand_id')
    .eq('user_id', userId)
    .in('role', ['owner', 'admin'])
    .eq('status', 'active');
  for (const row of bu || []) {
    if (row?.brand_id) ids.add(row.brand_id as string);
  }

  const { data: legacy } = await supabase
    .from('brands')
    .select('id')
    .eq('user_id', userId)
    .is('retired_at', null);
  for (const row of legacy || []) {
    if (row?.id) ids.add(row.id as string);
  }

  return Array.from(ids);
}

/**
 * Resolve the brand a user is authorized to manage.
 * Returns null if the user has no brand association.
 */
export async function resolveBrandForUser(userId: string): Promise<ResolvedBrand | null> {
  // 1. Primary: brand_users (supports multi-user brand teams)
  const { data: brandUser } = await supabase
    .from('brand_users')
    .select('brand_id')
    .eq('user_id', userId)
    .in('role', ['owner', 'admin'])
    .eq('status', 'active')
    .limit(1)
    .maybeSingle();

  if (brandUser?.brand_id) {
    const { data: activeMembershipBrand } = await supabase
      .from('brands')
      .select('id')
      .eq('id', brandUser.brand_id)
      .is('retired_at', null)
      .maybeSingle();
    if (activeMembershipBrand?.id) {
      return { brandId: activeMembershipBrand.id, source: 'brand_users' };
    }
  }

  // 2. Fallback: legacy brands.user_id direct ownership
  const { data: brand } = await supabase
    .from('brands')
    .select('id')
    .eq('user_id', userId)
    .is('retired_at', null)
    .maybeSingle();

  if (brand?.id) {
    return { brandId: brand.id, source: 'legacy_owner' };
  }

  return null;
}
